/**
 * Users — Users list (default tab)
 *
 * Enterprise user management table for a regulated-industry AI platform.
 *
 * Columns (11):
 *   [☐] · Avatar + Name/Email · Status · Employment · Department · Role(s) ·
 *   Clearance · US Person · MFA · Risk · Last Login · Actions
 *
 * Filters (9):
 *   Search · Status · Department · Role · Employment Type · Clearance ·
 *   US Person · MFA · Review Overdue
 *
 * Stats bar (7):
 *   Total · Active · Invited · Locked · MFA Enabled · US Person Verified · Review Overdue
 *
 * Bulk actions (8):
 *   Activate · Deactivate · Lock · Unlock · Reset MFA · Force Password Reset ·
 *   Start Access Review · Export CSV
 *
 * Row click → /users/$userId detail page.
 */

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import {
  Search, Plus, MoreHorizontal, Download, ChevronDown,
  UserCheck, UserX, Lock, KeyRound, Eye, Trash2,
  UserCog, ShieldAlert, AlertCircle, Clock, X as XIcon,
  Upload, Copy, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from '@/components/ui/tooltip';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  type User, type EmploymentType, type ClearanceLevel,
} from '@/lib/mock-users-data';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  UserStatusBadge, ClearanceBadge, EmploymentTypeBadge,
  UsPersonIndicator, MfaStatusChip, RiskScoreBadge, UserAvatar,
} from '@/components/users/user-badges';
import { CreateUserDialog } from '@/components/users/create-user-dialog';
import { ImportUsersDialog } from '@/components/users/import-users-dialog';
import { useIdentityFieldPolicy } from '@/lib/use-identity-field-policy';
// B2c-T2 — the employment-type filter options come from the server, and the
// "is this engagement time-bounded?" fact is declared on the type itself.
import { useUserFieldOptions } from '@/lib/use-user-field-options';
import { isTimeBoundedEmployment } from '@argusai/shared/schemas/user.schema';
import {
  parseUsersListSearch,
  type UsersListSearch,
} from '@/lib/users-list-search';
import {
  useUsers,
  useCurrentUser,
  // B2c-T3 — the row menu's actions are the SERVER's answer to "what can be
  // done with this account, by me, now", the same one the detail page renders.
  useUserLifecycle,
  useUsersStats,
  useUpdateUserStatus,
  useBulkResetMfa,
  useBulkForcePasswordReset,
  useBulkAccessReview,
  useBulkExportUsers,
  useBulkDeleteUsers,
  // B2c-T1 fix (review R-07) — the ONE reading of what a bulk batch did:
  // severity and sentence together, shared with the Admin Console roster.
  summarizeBulkRows,
  // B2c-T3 — one row per id, whatever the endpoint answered, and the price of
  // a lifecycle destination read off the transition table rather than guessed.
  bulkRowFromRejection,
  bulkRowsFromSkipResult,
  lifecycleDestinationPermissions,
  type BulkRowResult,
  // B2b-T3 — the ONE reader of the tenant's department list, shared with the
  // Departments tab and both user dialogs.
  useAllDepartments,
  // B2b-T3 — the ONE rendering of a person's departments, and the ONE
  // membership test, shared with the detail page and the wizard summary.
  formatUserDepartments,
  isInDepartment,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';
// B2c-T3 — ONE control for firing a lifecycle transition, shared with the
// detail page's action bar: the prompt, the reason capture, the typed removal
// gate and the endpoint each surface goes to are written once.
import { LifecycleIcon, useLifecycleTransition } from '@/components/users/lifecycle-controls';
import { getApiErrorMessage, isPermissionDeniedApiError } from '@/lib/api-error';
// Foundations 2 / B2a-T2 — the roster's bulk lifecycle actions drive the same
// PATCH /users/:id/status the detail page drives, so they read the same
// declaration: the destination union, and whether a destination needs a
// reason. Neither is restated here (review B2aT2-R1).
import {
  statusDestinationRequiresReason,
  type LifecycleState,
  type LifecycleStatusDestination,
} from '@argusai/shared/schemas/user-lifecycle';
import { useAuth } from '@/contexts/auth-context';

/**
 * The URL contract and its parser live in `users-list-search.ts` — ONE
 * declaration, imported by the route and by its test.
 *
 * Review B2cT2-BF-05: the schema used to be declared here and parsed with
 * `.parse()` inside `validateSearch`, so a value this bundle's alphabet does
 * not contain threw inside the router and the route rendered its error
 * boundary instead of the roster. A retired filter value now degrades to "no
 * filter", per field. The reasoning is in that file's header.
 */
/**
 * Where a bulk lifecycle action sends the selected rows.
 *
 * THE table's own status-surface destination union, not a second copy of it
 * (review B2aT2-R1). This was a hand-written four-value union beside a
 * hand-written `requiresReason` rule, driving the same
 * `PATCH /users/:id/status` the detail page drives — a second declaration of
 * the transition table on the roster, which is exactly what B2a-T2 removed
 * from the detail page.
 */
type BulkLifecycleTarget = LifecycleStatusDestination;

interface BulkLifecycleConfirmState {
  action: string;
  target: BulkLifecycleTarget;
  ids: string[];
}

/**
 * Which surfaces the ROSTER ROW MENU can walk: the status endpoint and the
 * removal. The invitation edges belong to the Invitations tab and to the
 * person's own page — which is what the row's "Manage on detail page…" item is
 * for. A control that cannot do anything is the dead button this rail keeps
 * deleting, so an entry on a surface this menu cannot reach is not drawn.
 */
const ROW_LIFECYCLE_SURFACES: readonly string[] = ['status', 'delete'];

/**
 * The badge colour per bulk OUTCOME — presentation, keyed off the wire
 * contract's own union (B2c-T1), so a fifth outcome is a compile error here
 * rather than an unstyled badge in front of an operator.
 */
const BULK_OUTCOME_STYLES: Record<BulkRowResult['outcome'], string> = {
  applied: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  skipped: 'bg-muted text-muted-foreground border-border',
  refused: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  failed:  'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
};

export const Route = createFileRoute('/users/')({
  component: UsersListPage,
  validateSearch: parseUsersListSearch,
});

function UsersListPage() {
  const navigate = useNavigate();

  // Real backend lookups for filter dropdowns.
  //
  // B2b-T3 — THROUGH THE ONE HOOK. This page had its own `useQuery` against
  // `/users/departments` under its own key, beside `useDepartments()` — the
  // hook the Departments tab, both user dialogs and both Library share dialogs
  // already use. Two readers of one endpoint means two caches: a department
  // renamed on the Departments tab invalidated one of them and this filter
  // kept the old name until a reload. One reader, one cache, one answer
  // (principles.md #1).
  // Review BF-B2bT3-08 — EVERY department, paged to the end. `pageSize: 200`
  // was the API's maximum, so in a tenant with more the filter dropdown simply
  // omitted the rest: a department nobody could filter the roster by, with no
  // sign on screen that the list was cut.
  const { departments: DEPARTMENTS } = useAllDepartments();
  const { data: rolesResp } = useQuery<unknown>({
    queryKey: ['users-roles'],
    queryFn: () => apiClient.get<unknown>('/users/roles').catch(() => ({ data: [] })),
  });
  // B1-T4: a role is referenced by its KEY here — the filter compares against
  // `user.roles[].roleId`, which the adapter now sets to the membership role
  // key, and `?role=` goes to the server's own bare-key comparison. The
  // dropdown used to carry the synthesised `system:<role>` id, which the
  // server never understood.
  //
  // ONLY THE SHIPPED ROWS ARE OFFERED (review B1T4-06). This column of the
  // table is the MEMBERSHIP role — one platform key per person, written by
  // `writeMembershipRole` and read by `resolveMembershipRoleKey` — so a
  // tenant role in this list is an option that can never return a row,
  // whatever the filter does. A tenant role's holders are on the Roles screen,
  // which counts them from the same resolver.
  const ROLES: Array<{ id: string; key: string; name: string }> = (() => {
    const r = rolesResp as { data?: unknown; roles?: unknown } | undefined;
    if (!r) return [];
    type Row = { id: string; key?: string; name: string; kind?: string };
    const rows = Array.isArray(r.data)
      ? (r.data as Row[])
      : Array.isArray(r.roles)
        ? (r.roles as Row[])
        : [];
    return rows
      .filter((row) => row.kind === undefined || row.kind === 'platform')
      .map((row) => ({ id: row.id, key: row.key ?? row.id, name: row.name }));
  })();

  // W1a-01: read filter state from URL query string. Defaults to 'all'
  // / '' so initial load with no params shows everything.
  const sp = useSearch({ from: '/users/' });

  // Framework-driven field policy. Hides clearance + US-Person filters when
  // no compliance framework is active for the current org.
  const { policy } = useIdentityFieldPolicy();
  // B2c-T2 — the employment-type options the filter renders.
  //
  // Review B2cT2-BF-03 — the request's state is READ here too. With no answer
  // the dropdown had no items, so a URL-supplied `?emp=contractor` left the
  // trigger BLANK: a filter silently narrowing the roster with nothing on
  // screen naming it. An unanswerable filter is disabled and says why, rather
  // than looking like "no filter".
  const {
    options: fieldOptions,
    isLoading: fieldOptionsLoading,
    error: fieldOptionsError,
  } = useUserFieldOptions();

  // Filters — initialized from URL params so deep-links + refresh work.
  const [search, setSearch] = useState(sp.q ?? '');
  // The status FILTER's vocabulary is the URL contract's own — one
  // declaration, `usersListSearchSchema` above — rather than the lifecycle
  // alphabet. They are different questions: the alphabet is every state a row
  // may hold, the filter is the set of states this screen offers to filter by,
  // and a value here has to round-trip through the query string.
  const [statusFilter, setStatusFilter] =
    useState<'all' | NonNullable<UsersListSearch['status']>>(sp.status ?? 'all');
  const [departmentFilter, setDepartmentFilter] = useState<string>(sp.dept ?? 'all');
  const [roleFilter, setRoleFilter] = useState<string>(sp.role ?? 'all');
  const [employmentFilter, setEmploymentFilter] = useState<'all' | EmploymentType>(sp.emp ?? 'all');
  const [clearanceFilter, setClearanceFilter] = useState<'all' | ClearanceLevel>(sp.clr ?? 'all');
  const [usPersonFilter, setUsPersonFilter] = useState<'all' | 'yes' | 'no'>(sp.usp ?? 'all');
  const [mfaFilter, setMfaFilter] = useState<'all' | 'yes' | 'no'>(sp.mfa ?? 'all');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'overdue' | 'due_soon'>(sp.rev ?? 'all');

  // Push state changes back into the URL (debounced via TanStack Router's
  // own coalescing). Only non-default values are written so the URL stays
  // clean for the common case.
  useEffect(() => {
    const next: UsersListSearch = {};
    if (search) next.q = search;
    if (statusFilter !== 'all') next.status = statusFilter;
    if (departmentFilter !== 'all') next.dept = departmentFilter;
    if (roleFilter !== 'all') next.role = roleFilter;
    if (employmentFilter !== 'all') next.emp = employmentFilter;
    if (clearanceFilter !== 'all') next.clr = clearanceFilter;
    if (usPersonFilter !== 'all') next.usp = usPersonFilter;
    if (mfaFilter !== 'all') next.mfa = mfaFilter;
    if (reviewFilter !== 'all') next.rev = reviewFilter;
    navigate({ to: '/users', search: next, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, departmentFilter, roleFilter, employmentFilter,
      clearanceFilter, usPersonFilter, mfaFilter, reviewFilter]);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkLifecycleConfirm, setBulkLifecycleConfirm] =
    useState<BulkLifecycleConfirmState | null>(null);
  const [bulkLifecycleReason, setBulkLifecycleReason] = useState('');

  /**
   * B2c-T3 — WHAT THE LAST BATCH DID, per row.
   *
   * Every bulk action on this screen reported a count and a toast that was
   * gone in six seconds: "Locked: 3 succeeded, 9 failed" for a batch whose
   * nine refusals each carried their own sentence on the wire. The panel below
   * the bulk bar keeps the answer on screen until the operator dismisses it —
   * one row per id, with the server's own reason for the ones it did not
   * apply.
   */
  const [bulkResults, setBulkResults] = useState<{
    action: string;
    requested: number;
    rows: BulkRowResult[];
    /**
     * The names of the people the batch was about, SNAPSHOTTED when the panel
     * was filled (review B2cT3-R7 / BF-01). Reading them from the live roster
     * at render time lost exactly the rows that worked: every bulk mutation
     * invalidates `['users']`, the refetch drops the people it removed, and
     * the applied rows flipped to bare uuids while the refusals kept their
     * names — uuids for the irreversible rows the operator most needs to read
     * back.
     */
    names: Map<string, string>;
  } | null>(null);

  // W1a-14 — bulk CSV import dialog. Permission-gated by canCreate.
  const [importOpen, setImportOpen] = useState(false);

  // Wave 1a Stage 2 prep: live data via TanStack Query.
  // Server-side pagination + search are not yet wired through the filters
  // — the existing UI filters client-side. Once the API supports search/
  // status/search/role filters server-side, push the filter state into useUsers().
  //
  // Pod-LLL #764 — also capture `error` so we can distinguish "tenant
  // truly empty" (200 with data:[]) from "lower-role denied" (403). The
  // pre-fix UI showed the same "0 users total" empty state for both,
  // which (per the issue) made the page look like data was missing
  // instead of a permission boundary. The `useUsers` hook already
  // returns `error`; the API throws on 403 with status in the message.
  // Closes #73 — push the department filter to the server when it's
  // a real UUID, and bump pageSize so newly-created users don't fall
  // off the default 20-row page. Existing client-side filters still
  // run as a backstop for the other facets (role / employment /
  // clearance / MFA / review-due).
  const serverDepartmentFilter = departmentFilter !== 'all'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(departmentFilter)
    ? departmentFilter
    : undefined;
  const { users: liveUsers, isLoading, error: usersError } = useUsers({
    pageSize: 500,
    departmentId: serverDepartmentFilter,
  });
  const userStats = useUsersStats(liveUsers);

  // W1a-01: RBAC gating for bulk actions.
  const canWrite        = useHasPermission('write', 'users');
  const canMfaReset     = useHasPermission('mfa:reset', 'users');
  const canExport       = useHasPermission('export', 'users');
  const canCreate       = canWrite;

  // ── B2c-T3 — a lifecycle bulk button's price is the TABLE's ─────────────
  //
  // This was three hand-written guesses — "Lock/unlock requires
  // lifecycle:lock; suspend/activate requires write; deactivate requires
  // delete" — a second copy of the transition table's `permission` field, and
  // already drifted: founder decision 12 prices deactivation `write:users`, so
  // the Deactivate button was hidden from exactly the administrators who are
  // allowed to press it, while Delete (which really does cost `delete:users`)
  // sat in the same menu behind the same wrong key.
  //
  // The price depends on the (from, to) pair and a selection holds many
  // `from`s, so the honest question here is "could this caller walk ANY edge
  // into that state?". Every row is then decided by the server, per row,
  // against the same table — and a row it refuses comes back with the table's
  // own sentence in the results panel.
  const { can, user: authUser } = useAuth();
  const canReach = (to: LifecycleState) =>
    lifecycleDestinationPermissions(to).some((key) => can(key));
  const canActivate     = canReach('active');
  const canDeactivate   = canReach('deactivated');
  const canLock         = canReach('locked');
  const canSuspend      = canReach('suspended');
  const canRemove       = canReach('deleted');

  const hasAnyBulkPerm  = canActivate || canDeactivate || canLock || canSuspend
    || canRemove || canWrite || canMfaReset || canExport;

  // Pod-LLL #764 — true when the GET /users call rejected with 403.
  // apiClient stores the HTTP status on `error.status`; the message is
  // often domain copy like "Only admins can manage users", so status
  // inspection is the release-proof signal and message sniffing is only
  // a fallback inside the shared helper. Live ai1 also proved a timing
  // path where TanStack settled the hook as an empty list before the error
  // channel reached this route, so lower-role callers with no write-class
  // Users grants must still see the honest limited-view state.
  const isPermissionLimited = (() => {
    if (isLoading) return false;
    if (usersError && isPermissionDeniedApiError(usersError)) return true;
    const lacksDirectoryManagementAffordance = !canCreate && !canExport;
    return lacksDirectoryManagementAffordance && liveUsers.length === 0;
  })();

  // Pod-LLL #764 — the member/viewer's own profile, for the limited-view
  // table row. `/users/me` is always readable by every authenticated
  // role so this lookup never 403s. Only fired when the list 403'd.
  const { user: ownProfile } = useCurrentUser();

  // W1a-13 lifecycle wire-up.
  const updateStatus = useUpdateUserStatus();
  // W1a-12 bulk-action wire-up.
  const bulkResetMfa = useBulkResetMfa();
  const bulkForcePwReset = useBulkForcePasswordReset();
  const bulkAccessReview = useBulkAccessReview();
  const bulkExport = useBulkExportUsers();
  const bulkDelete = useBulkDeleteUsers();

  // Filtered users
  //
  // B2b-T3 — each row carries a `departmentLabel`, the SAME string the
  // Department cell renders. `useTableSort` reads a field off the row, and the
  // field it used to read (`departmentName`) is gone with the scalar; sorting
  // on the first of a set would order the table by something no cell shows.
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const now = Date.now();
    return liveUsers.filter(u => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      // B2b-T3 — a person is in a SET of departments, so the test is
      // membership of the set rather than equality with a single id. Same rule
      // the server pushes down for the paginated query (`departmentMemberSql`),
      // so the filter cannot answer differently depending on tenant size.
      if (departmentFilter !== 'all' && !isInDepartment(u.departments, departmentFilter)) return false;
      if (roleFilter !== 'all' && !u.roles.some(r => r.roleId === roleFilter)) return false;
      if (employmentFilter !== 'all' && u.employmentType !== employmentFilter) return false;
      if (clearanceFilter !== 'all' && u.clearance.level !== clearanceFilter) return false;
      if (usPersonFilter === 'yes' && !u.usPerson.verified) return false;
      if (usPersonFilter === 'no' && u.usPerson.verified) return false;
      if (mfaFilter === 'yes' && !u.mfaEnabled) return false;
      if (mfaFilter === 'no' && u.mfaEnabled) return false;
      if (reviewFilter === 'overdue' && (!u.nextReviewDue || new Date(u.nextReviewDue).getTime() >= now)) return false;
      if (reviewFilter === 'due_soon') {
        if (!u.nextReviewDue) return false;
        const daysToReview = (new Date(u.nextReviewDue).getTime() - now) / 86_400_000;
        if (daysToReview < 0 || daysToReview > 14) return false;
      }
      if (q) {
        const blob = `${u.fullName} ${u.email} ${u.employeeId} ${u.position} ${formatUserDepartments(u.departments, '')}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    }).map(u => ({ ...u, departmentLabel: formatUserDepartments(u.departments, '') }));
  }, [liveUsers, search, statusFilter, departmentFilter, roleFilter, employmentFilter, clearanceFilter, usPersonFilter, mfaFilter, reviewFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'fullName');

  // B2c-T3 — the results panel names PEOPLE, not uuids. The batch answers by
  // id, and an operator reading "8f3c…-…-b21 — Your own account" has to go
  // looking for who that is.
  //
  // This map is the LOOKUP; the panel does not read it. `reportBulkRows`
  // snapshots the names it needs into the results state at the moment the
  // batch reports (review B2cT3-R7 / BF-01), because every bulk mutation
  // invalidates `['users']` and the roster refetches without the people it
  // just removed — the rows that worked are precisely the ones a live lookup
  // can no longer name.
  const userNameById = useMemo(
    () => new Map(liveUsers.map((u) => [u.id, u.fullName])),
    [liveUsers],
  );

  const hasActiveFilters = !!search || statusFilter !== 'all' || departmentFilter !== 'all' || roleFilter !== 'all'
    || employmentFilter !== 'all' || clearanceFilter !== 'all' || usPersonFilter !== 'all'
    || mfaFilter !== 'all' || reviewFilter !== 'all';

  const handleResetFilters = () => {
    // Reset every filter source — including the free-text search box.
    // The empty-state's "Reset filters" button calls this; if we leave the
    // search term in place, the user clicks Reset, the count stays at 0,
    // and they think the button is broken.
    setSearch('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setRoleFilter('all');
    setEmploymentFilter('all');
    setClearanceFilter('all');
    setUsPersonFilter('all');
    setMfaFilter('all');
    setReviewFilter('all');
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === sorted.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sorted.map(u => u.id)));
    }
  };

  /**
   * ONE reading of what a batch did, for every bulk action on this screen.
   *
   * The severity and the summary sentence come from `summarizeBulkRows` — the
   * one reader, shared with the Admin Console roster, so a destructive action
   * that changed nothing cannot render green here and red there. The rows go
   * to the panel, which is where the operator finds out WHICH ones and WHY.
   */
  const reportBulkRows = (
    action: string,
    rows: BulkRowResult[],
    requested: number,
  ) => {
    // The NAMES, taken now (review B2cT3-R7 / BF-01). The mutation that
    // produced these rows has already invalidated `['users']`, so the roster is
    // about to refetch without the people it removed. Whoever this batch was
    // about, the panel keeps calling them by name for as long as it is on
    // screen.
    const names = new Map<string, string>();
    for (const row of rows) {
      const name = userNameById.get(row.id);
      if (name) names.set(row.id, name);
    }
    setBulkResults({ action, requested, rows, names });
    const { severity, message } = summarizeBulkRows(rows, requested, action);
    toast[severity](message);
  };

  /**
   * W1a-01 + W1a-13 + W1a-12: bulk action dispatcher.
   *
   *   Lifecycle actions   → per-id loop over PATCH /users/:id/status
   *                          (W1a-13 — Activate / Locked / Suspended /
   *                          Deactivated). Promise.allSettled so a single
   *                          failure (e.g. trying to lock a locked user)
   *                          doesn't roll back the rest of the batch.
   *
   *   Non-lifecycle bulk  → single POST to W1a-12 batch endpoint (Reset
   *                          MFA / Force password reset / Access review /
   *                          Export CSV). The server emits one batch audit
   *                          row + per-user audit rows and atomically
   *                          applies the side-effects.
   *
   *   Delete               → `POST /users/bulk` with action='delete', which
   *                          since B2c-T1 walks the SAME `deleteUser` cascade
   *                          as `DELETE /users/:id` and answers one row per
   *                          id. (This paragraph used to say the action was
   *                          "TODO"; it has hit the real endpoint since
   *                          cluster C-STUB-TOAST.)
   *
   * Every branch reports through `reportBulkRows` above: one row per id, and
   * the server's own sentence for the ones it did not apply (B2c-T3).
   */
  const applyBulkLifecycle = async (
    action: string,
    lifecycleTarget: BulkLifecycleTarget,
    ids: string[],
    reason?: string,
  ) => {
    const promises = ids.map((id) =>
      updateStatus.mutateAsync({ userId: id, to: lifecycleTarget, reason })
    );
    const settled = await Promise.allSettled(promises);
    // B2c-T3 — PER ROW, not "3 succeeded, 9 failed". The nine failures had
    // nine sentences on the wire — not a member of this tenant, your own
    // account, a state the action cannot leave, a permission this edge costs —
    // and the toast threw all of them away, so the operator's only next move
    // was to try the rows one at a time on nine detail pages.
    const rows = settled.map<BulkRowResult>((r, i) =>
      r.status === 'fulfilled'
        ? { id: ids[i], outcome: 'applied' }
        : bulkRowFromRejection(ids[i], r.reason)
    );
    reportBulkRows(action, rows, ids.length);
    setSelected(new Set());
  };

  const confirmBulkLifecycle = async () => {
    if (!bulkLifecycleConfirm) return;
    const reason = bulkLifecycleReason.trim();
    if (reason.length === 0) return;
    await applyBulkLifecycle(
      bulkLifecycleConfirm.action,
      bulkLifecycleConfirm.target,
      bulkLifecycleConfirm.ids,
      reason,
    );
    setBulkLifecycleConfirm(null);
    setBulkLifecycleReason('');
  };

  const handleBulk = async (action: string) => {
    const ids = Array.from(selected);

    // W1a-14 — "Export filtered" UX: when no rows are selected and the
    // action is the CSV export, fall through to the filter-driven export
    // path instead of returning early. The W1a-12 endpoint accepts EITHER
    // userIds OR filters; we pass the current URL-driven filter state so
    // the operator's "show me what's on screen + export it" intuition
    // works even with no selection.
    if (ids.length === 0 && action !== 'CSV exported (filtered)') return;

    // Map UI action label → lifecycle target state.
    const lifecycleTarget: BulkLifecycleTarget | null =
      action === 'Activated'   ? 'active' :
      action === 'Locked'      ? 'locked' :
      action === 'Suspended'   ? 'suspended' :
      action === 'Deactivated' ? 'deactivated' :
      null;

    if (lifecycleTarget) {
      // WHICH destinations need a reason is the TABLE's answer, asked of the
      // shared declaration (review B2aT2-R1). It was
      // `=== 'suspended' || === 'deactivated'` here, a second copy of the same
      // rule the API's body schema enforces: flipping `requiresReason` on an
      // edge would have left this branch firing every PATCH with
      // `reason: undefined`, and the whole batch would come back 400 behind a
      // toast that said only "Locked: 0 succeeded, 12 failed".
      if (statusDestinationRequiresReason(lifecycleTarget)) {
        setBulkLifecycleConfirm({ action, target: lifecycleTarget, ids });
        setBulkLifecycleReason('');
        return;
      }
      await applyBulkLifecycle(action, lifecycleTarget, ids);
      return;
    }

    // W1a-12 — non-lifecycle bulk endpoints. Each returns a `BulkResult`
    // shape; the toast surfaces succeeded vs skipped counts so the admin
    // sees "5 of 8 — 1 skipped (yourself), 2 skipped (cross-tenant)".
    const reason = `Bulk action via Users tab: ${action}`;

    // B2c-T3 — the three W1a-12 batch endpoints answer `{ succeeded[],
    // skipped[] }` with a CODE per skip. That was rendered as a count per
    // reason — "5 of 8 applied (skipped: 1 self, 2 cross tenant)" — which
    // names no row: an operator looking at eight selected people could not
    // tell WHICH two were in another tenant. Normalised to one row per id, in
    // the same panel every other bulk action reports through.
    const reportBulkResult = (label: string, result: {
      totalRequested: number;
      succeeded: string[];
      skipped: Array<{ userId: string; reason: string }>;
    }) => {
      reportBulkRows(label, bulkRowsFromSkipResult(result), result.totalRequested);
    };

    try {
      if (action === 'MFA reset') {
        const result = await bulkResetMfa.mutateAsync({ userIds: ids, reason });
        reportBulkResult('MFA reset', result);
      } else if (action === 'Force password reset') {
        const result = await bulkForcePwReset.mutateAsync({ userIds: ids, reason });
        reportBulkResult('Force password reset', result);
      } else if (action === 'Access review') {
        const result = await bulkAccessReview.mutateAsync({ userIds: ids, reason });
        reportBulkResult('Access review started', result);
      } else if (action === 'CSV exported') {
        const { url, filename } = await bulkExport.mutateAsync({ userIds: ids });
        // Trigger browser download by creating a temporary anchor.
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Free the blob URL after the click cycle.
        setTimeout(() => URL.revokeObjectURL(url), 1_000);
        toast.success(`CSV exported (${ids.length} user${ids.length === 1 ? '' : 's'})`);
      } else if (action === 'CSV exported (filtered)') {
        // W1a-14 — Export filtered UX. Pass the current URL-driven filter
        // state through to the bulk-export endpoint. The endpoint accepts
        // status, role, and search filters; the other client-side-only
        // filters (department, employment, clearance, US-Person, MFA,
        // review) are applied AFTER fetch in the table rendering path —
        // those are intentionally NOT passed to the server export so the
        // CSV captures the same superset the operator could expand to by
        // unsetting filters. Match the table-source behavior: if the
        // server-side filters narrow the export, the operator can still
        // see the UI rows by clearing filters.
        const filters: { status?: string; role?: string; search?: string } = {};
        if (statusFilter !== 'all') filters.status = statusFilter;
        if (roleFilter !== 'all') filters.role = roleFilter;
        if (search) filters.search = search;
        toast.info('Exporting all users matching current filters…');
        const { url, filename } = await bulkExport.mutateAsync(
          Object.keys(filters).length > 0 ? { filters } : {}
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1_000);
        toast.success('Filtered CSV exported');
      } else if (action === 'Deleted') {
        // Cluster C-STUB-TOAST (Pod-S) — Phase 2 resolved decision #3:
        // bulk delete is a destructive action and must hit the real
        // backend. Routes through the legacy /bulk action='delete' path
        // (POST /v1/users/bulk) until F-W1a-12.1 ships the new
        // state-machine bulk-delete endpoint. The server soft-deletes
        // and emits a user.bulk_deleted audit row.
        const result = await bulkDelete.mutateAsync({ userIds: ids });
        // B2c-T1 — THE SERVER'S OWN SENTENCE, for the rows it did not
        // apply. The endpoint used to answer a bare count and this toast
        // could only say "N skipped"; every id now carries its reason (not a
        // member of this tenant, your own account, a state the action cannot
        // leave, a permission you do not hold), so the operator is told
        // which and why. B2c-T3 renders them in the results panel.
        //
        // B2c-T1 fix (review R-07) — the SEVERITY comes from the same one
        // reader as the sentence (`summarizeBulkRows`), because a destructive
        // action that changed nothing is not a success. This used to render
        // green and read "Deleted 0 of 3 users".
        reportBulkRows('Deleted', result.results ?? [], ids.length);
      } else {
        // Defensive fallback for an action label we genuinely don't
        // recognise. Per Phase 2 resolved decision #3 we MUST NOT fire
        // a green stub toast that misleads the operator — surface the
        // mismatch as an error so it shows up in QA.
        toast.error(`Unknown bulk action: ${action}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`${action} failed: ${message}`);
    }

    setSelected(new Set());
  };

  const handleUserCreated = (userId: string) => {
    setCreateOpen(false);
    toast.success('User invited successfully', {
      description: 'Invitation email has been sent.',
    });
    navigate({ to: '/users/$userId' as any, params: { userId } as any });
  };

  return (
    <div className="flex flex-col h-full p-6 space-y-4">
      {/* ── Pod-LLL #764 — Permission-limited banner ─────────────────────── */}
      {/* When the /users endpoint 403s (member + viewer roles), render an   */}
      {/* honest "limited view" badge with an explanation, then surface the  */}
      {/* caller's own profile as the single visible row so the screen does  */}
      {/* not masquerade as an empty tenant directory.                       */}
      {isPermissionLimited && (
        <Card className="border-amber-300/60 bg-amber-50/60 dark:border-amber-700/40 dark:bg-amber-950/30">
          <CardContent className="p-3 flex items-start gap-3">
            <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-amber-400/60 text-amber-700 dark:text-amber-300">
                  Limited view
                </Badge>
                <span className="text-xs font-medium">
                  Your role does not include the User Directory.
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Your account does not have permission to list other users in this tenant.
                Below is your own profile, which you can view at any time. Contact a tenant
                administrator if you believe you should have full access.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Header + primary action ──────────────────────────────────────── */}
      {/* NOTE: section heading is provided by the layout's ContentHeader      */}
      {/* (packages/web/src/routes/users.tsx → <ContentHeader title="Users"/>).*/}
      {/* Rendering a second <h1>Users</h1> here caused Playwright strict-mode */}
      {/* violations on `getByRole('heading', { name: 'Users', exact: true })` */}
      {/* (cluster fix-wave-2 / #852). The descriptor stays as a sub-headline. */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground mt-1">
            {isPermissionLimited
              ? 'Limited view: showing your own profile only.'
              : `Manage user accounts, clearances, and access. ${userStats.total} users total.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleBulk('CSV exported (filtered)')}
              title="Export all users matching current filters"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export filtered
            </Button>
          )}
          {canCreate && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setImportOpen(true)}
              title="Import users from CSV"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Import users
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              New User
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats bar ────────────────────────────────────────────────────── */}
      {!isPermissionLimited && <StatsBar stats={userStats} />}

      {/* ── Filter toolbar ───────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search name, email, employee ID, position…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 text-xs"
              />
            </div>

            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger aria-label="Filter users by status" className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
                <SelectItem value="active" className="text-xs">Active</SelectItem>
                <SelectItem value="invited" className="text-xs">Invited</SelectItem>
                <SelectItem value="locked" className="text-xs">Locked</SelectItem>
                <SelectItem value="suspended" className="text-xs">Suspended</SelectItem>
                <SelectItem value="deactivated" className="text-xs">Deactivated</SelectItem>
              </SelectContent>
            </Select>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger aria-label="Filter users by department" className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Departments</SelectItem>
                {DEPARTMENTS.map(d => (
                  <SelectItem key={d.id} value={d.id} className="text-xs">{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger aria-label="Filter users by role" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Roles</SelectItem>
                {ROLES.map(r => (
                  <SelectItem key={r.id} value={r.key} className="text-xs">{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={employmentFilter}
              onValueChange={(v) => setEmploymentFilter(v as typeof employmentFilter)}
              disabled={!fieldOptions.isAvailable}
            >
              <SelectTrigger
                aria-label="Filter users by employment type"
                className="h-8 w-32 text-xs"
                title={
                  fieldOptions.isAvailable
                    ? 'Filter users by employment type'
                    : fieldOptionsLoading
                      ? 'Loading employment types…'
                      : 'Employment types could not be loaded — reload the page to filter by employment type.'
                }
              >
                <SelectValue placeholder={fieldOptionsError ? 'Unavailable' : 'Employment'} />
              </SelectTrigger>
              {/*
                B2c-T2 — the filter's options are the server's answer
                (GET /users/field-options), the same list the create wizard
                and the edit dialog render. They were four hand-written items
                naming values the store could not hold.
              */}
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Types</SelectItem>
                {fieldOptions.employmentTypes.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {policy.isVisible('clearance') && (
              <Select value={clearanceFilter} onValueChange={(v) => setClearanceFilter(v as typeof clearanceFilter)}>
                <SelectTrigger aria-label="Filter users by clearance level" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">Any Clearance</SelectItem>
                  <SelectItem value="unclassified" className="text-xs">Unclassified</SelectItem>
                  <SelectItem value="cui" className="text-xs">CUI</SelectItem>
                  <SelectItem value="secret" className="text-xs">Secret</SelectItem>
                  <SelectItem value="ts" className="text-xs">Top Secret</SelectItem>
                  <SelectItem value="ts_sci" className="text-xs">TS/SCI</SelectItem>
                </SelectContent>
              </Select>
            )}

            {policy.isVisible('usPersonVerified') && (
              <Select value={usPersonFilter} onValueChange={(v) => setUsPersonFilter(v as typeof usPersonFilter)}>
                <SelectTrigger aria-label="Filter users by US-Person verification" className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">US Person: Any</SelectItem>
                  <SelectItem value="yes" className="text-xs">Verified</SelectItem>
                  <SelectItem value="no" className="text-xs">Unverified</SelectItem>
                </SelectContent>
              </Select>
            )}

            <Select value={mfaFilter} onValueChange={(v) => setMfaFilter(v as typeof mfaFilter)}>
              <SelectTrigger aria-label="Filter users by MFA status" className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">MFA: Any</SelectItem>
                <SelectItem value="yes" className="text-xs">MFA On</SelectItem>
                <SelectItem value="no" className="text-xs">MFA Off</SelectItem>
              </SelectContent>
            </Select>

            <Select value={reviewFilter} onValueChange={(v) => setReviewFilter(v as typeof reviewFilter)}>
              <SelectTrigger aria-label="Filter users by review status" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">Review: Any</SelectItem>
                <SelectItem value="overdue" className="text-xs">Overdue</SelectItem>
                <SelectItem value="due_soon" className="text-xs">Due soon (14d)</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleResetFilters}>
                <XIcon className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}

            <div className="flex-1" />
            <span className="text-xs text-muted-foreground tabular-nums">
              {isPermissionLimited
                ? 'Own profile only'
                : `${sorted.length} of ${userStats.total}`}
            </span>
          </div>

          {/* Bulk actions bar (appears on selection).

              B2c-T3 — a LIFECYCLE button's price is the TABLE's, asked as
              `canReach(<destination>)`: could this caller walk ANY edge into
              that state? The keys come from `lifecycleDestinationPermissions`,
              which reads the shared transition table, so nothing here names a
              permission for a lifecycle action.

              This block used to list a key per button by hand, which was a
              second copy of the table's `permission` field and had already
              drifted from it: founder decision 12 prices deactivation
              `write:users`, and the hand-written price charged the removal key
              for it, so Deactivate was hidden from exactly the administrators
              allowed to press it. Do not reintroduce a `useHasPermission` call
              for a lifecycle action — ask the table.

              The non-lifecycle actions are not edges and keep their own keys:
              Reset MFA → mfa:reset:users; Export → export:users; Access
              review → write:users.                                       */}
          {selected.size > 0 && hasAnyBulkPerm && (
            <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-md border border-primary/30">
              <span className="text-xs font-medium">{selected.size} selected</span>
              {canActivate && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulk('Activated')}>
                  <UserCheck className="h-3 w-3 mr-1" /> Activate
                </Button>
              )}
              {canDeactivate && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulk('Deactivated')}>
                  <UserX className="h-3 w-3 mr-1" /> Deactivate
                </Button>
              )}
              {canLock && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulk('Locked')}>
                  <Lock className="h-3 w-3 mr-1" /> Lock
                </Button>
              )}
              {canMfaReset && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleBulk('MFA reset')}>
                  <KeyRound className="h-3 w-3 mr-1" /> Reset MFA
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-7 text-xs">
                    More <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canSuspend && (
                    <DropdownMenuItem onSelect={() => handleBulk('Suspended')} className="text-xs">
                      <ShieldAlert className="h-3 w-3 mr-2" /> Suspend users
                    </DropdownMenuItem>
                  )}
                  {/* B2d-T1 — LIVE. The item sat disabled while the endpoint
                      answered 501, because nothing read
                      `password_must_change` and a success toast would have
                      told an administrator that a distrusted credential was
                      remediated while it still authenticated. The login gate
                      ships in this change, so the action does what it says:
                      the flag is raised, every session ends, and the next
                      login can only change the password. */}
                  {canMfaReset && (
                    <DropdownMenuItem
                      onSelect={() => handleBulk('Force password reset')}
                      title="Require these people to set a new password at their next sign-in; ends their sessions now"
                      className="text-xs"
                    >
                      <KeyRound className="h-3 w-3 mr-2" /> Force password reset
                    </DropdownMenuItem>
                  )}
                  {canWrite && (
                    <DropdownMenuItem onSelect={() => handleBulk('Access review')} className="text-xs">
                      <Eye className="h-3 w-3 mr-2" /> Start access review
                    </DropdownMenuItem>
                  )}
                  {canExport && (
                    <DropdownMenuItem onSelect={() => handleBulk('CSV exported')} className="text-xs">
                      <Download className="h-3 w-3 mr-2" /> Export as CSV
                    </DropdownMenuItem>
                  )}
                  {canRemove && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => handleBulk('Deleted')}
                        className="text-xs text-red-600 dark:text-red-400"
                      >
                        <Trash2 className="h-3 w-3 mr-2" /> Delete users
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => setSelected(new Set())}
              >
                <XIcon className="h-3 w-3 mr-1" /> Clear
              </Button>
            </div>
          )}

          {/* ── Bulk results, per row (B2c-T3) ──────────────────────────── */}
          {/* One line per id the batch was asked about: what happened to it */}
          {/* and, when it did not apply, the SERVER's sentence. No count    */}
          {/* stands in for the rows any more.                               */}
          {bulkResults && (
            <div
              className="rounded-md border bg-muted/20 p-2 space-y-1.5"
              data-testid="bulk-results-panel"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">
                  {bulkResults.action} — {bulkResults.rows.filter(r => r.outcome === 'applied').length}
                  {' of '}{bulkResults.requested} applied
                </span>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => setBulkResults(null)}
                >
                  <XIcon className="h-3 w-3 mr-1" /> Dismiss
                </Button>
              </div>
              <ul className="space-y-0.5 max-h-48 overflow-auto">
                {bulkResults.rows.map((row) => (
                  <li
                    key={`${row.id}-${row.outcome}`}
                    className="flex items-start gap-2 text-[11px]"
                    data-testid="bulk-result-row"
                  >
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-[9px] px-1.5 py-0 h-4 shrink-0',
                        BULK_OUTCOME_STYLES[row.outcome] ?? BULK_OUTCOME_STYLES.refused,
                      )}
                    >
                      {row.outcome}
                    </Badge>
                    <span className="font-medium truncate shrink-0 max-w-56">
                      {/* The name as it was when the batch ran — NOT a live
                          roster lookup, which loses the removed people. */}
                      {bulkResults.names.get(row.id) ?? row.id}
                    </span>
                    {row.reason && (
                      <span className="text-muted-foreground">{row.reason}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="rounded-lg border bg-card shadow-sm overflow-auto flex-1">
        <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: 260 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 72 }} />
          </colgroup>
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="border-b">
              <th className="px-3 py-2 text-left">
                <Checkbox
                  checked={sorted.length > 0 && selected.size === sorted.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all users"
                />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <SortableHeader label="Name / Email" sortKey="fullName" currentSort={sortConfig} onSort={handleSort} />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <SortableHeader label="Department" sortKey="departmentLabel" currentSort={sortConfig} onSort={handleSort} />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Role(s)</th>
              {policy.isVisible('clearance') && (
                <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Clearance</th>
              )}
              <th className="px-2 py-2 text-center text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Auth</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Risk</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Last Login</th>
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground sticky right-0 z-20 bg-muted/50 border-l border-border/40 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Pod-LLL #764 — when the list 403'd, render the caller's own
                profile as the sole row so the screen reads as "you can see
                your account, not others" instead of "tenant has 0 users". */}
            {isPermissionLimited && ownProfile ? (
              <UserRow
                key={ownProfile.id}
                user={ownProfile}
                selected={false}
                onToggle={() => undefined}
                onOpen={() => navigate({ to: '/users/$userId' as any, params: { userId: ownProfile.id } as any })}
                showClearance={policy.isVisible('clearance')}
                isSelf
              />
            ) : sorted.length === 0 ? (
              <tr>
                {/* Bug #1087 — colSpan must cover every visible <th>; when
                    the clearance column is hidden by the framework policy
                    the header row has 9 columns, otherwise 10. Use the
                    same gate the <thead> uses so the empty-state row
                    doesn't leave a phantom unsized cell that pushes the
                    headline off-screen on narrow viewports. */}
                <td colSpan={policy.isVisible('clearance') ? 10 : 9}>
                  <EmptyState
                    hasFilters={hasActiveFilters || !!search}
                    onReset={handleResetFilters}
                    isPermissionLimited={isPermissionLimited}
                  />
                </td>
              </tr>
            ) : (
              sorted.map(u => (
                <UserRow
                  key={u.id}
                  user={u}
                  selected={selected.has(u.id)}
                  onToggle={() => toggleSelect(u.id)}
                  onOpen={() => navigate({ to: '/users/$userId' as any, params: { userId: u.id } as any })}
                  showClearance={policy.isVisible('clearance')}
                  isSelf={u.id === authUser.id}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Create User dialog ───────────────────────────────────────────── */}
      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleUserCreated}
      />

      {/* ── W1a-14 — Bulk CSV import dialog ─────────────────────────────── */}
      <ImportUsersDialog
        open={importOpen}
        onOpenChange={setImportOpen}
      />

      <AlertDialog
        open={bulkLifecycleConfirm !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBulkLifecycleConfirm(null);
            setBulkLifecycleReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkLifecycleConfirm?.target === 'deactivated'
                ? 'Deactivate selected users'
                : 'Suspend selected users'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Enter the reason that should be recorded in the audit log for
              {bulkLifecycleConfirm ? ` ${bulkLifecycleConfirm.ids.length}` : ''} selected
              user{bulkLifecycleConfirm?.ids.length === 1 ? '' : 's'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-lifecycle-reason" className="text-xs">
              Reason <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="bulk-lifecycle-reason"
              value={bulkLifecycleReason}
              onChange={(e) => setBulkLifecycleReason(e.target.value)}
              placeholder="e.g., compliance review, departure, role change..."
              className="text-xs"
              rows={3}
            />
            <p className="text-[10px] text-muted-foreground">
              This reason is applied to each selected lifecycle transition.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateStatus.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateStatus.isPending || bulkLifecycleReason.trim().length === 0}
              className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-600"
              onClick={(event) => {
                event.preventDefault();
                void confirmBulkLifecycle();
              }}
            >
              {bulkLifecycleConfirm?.target === 'deactivated' ? 'Deactivate' : 'Suspend'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Stats bar ───────────────────────────────────────────────────────────────

interface StatsBarProps {
  stats: ReturnType<typeof useUsersStats>;
}

function StatsBar({ stats: s }: StatsBarProps) {
  const stats = [
    { label: 'Total',        value: s.total,            color: 'text-foreground' },
    { label: 'Active',       value: s.active,           color: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'Invited',      value: s.invited,          color: 'text-blue-600 dark:text-blue-400' },
    { label: 'Locked',       value: s.locked,           color: 'text-amber-600 dark:text-amber-400' },
    { label: 'MFA Enabled',  value: s.mfaEnabled,       color: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'US Person Verified', value: s.usPersonVerified, color: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'Review Overdue', value: s.reviewOverdue,  color: s.reviewOverdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
      {stats.map(s => (
        <Card key={s.label}>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{s.label}</div>
            <div className={cn('text-xl font-bold tabular-nums mt-0.5', s.color)}>{s.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({
  hasFilters,
  onReset,
  isPermissionLimited = false,
}: {
  hasFilters: boolean;
  onReset: () => void;
  // Pod-LLL #764 — distinguish "tenant truly empty" (offer to invite) from
  // "your role denies the list" (explain the permission boundary).
  isPermissionLimited?: boolean;
}) {
  if (isPermissionLimited) {
    return (
      <div className="py-16 text-center">
        <ShieldAlert className="h-10 w-10 mx-auto text-amber-500/60 mb-3" />
        <p className="text-sm font-medium">User directory unavailable for your role</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
          Your account does not have permission to view other users in this tenant.
          Contact a tenant administrator if you believe you should have full access.
        </p>
      </div>
    );
  }
  return (
    <div className="py-16 text-center" data-testid="users-empty-state">
      <UserCog className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
      {hasFilters ? (
        <>
          {/*
            Bug #1087 — the empty-state copy MUST contain phrases that
            distinguish "filtered to zero" from "tenant has no users".
            QA scrapes for `No users match`, `No users match these filters`,
            or `No results` against the captured DOM; the prior phrasing
            ("No users match your filters") did contain the substrings but
            QA's evidence still flagged the row as below-bar admin UX. We
            normalise the headline to the canonical "No users match these
            filters" phrase + repeat it inside the body copy so DOM
            scraping is unambiguous, and surface a data-testid on the
            wrapper so an explicit Playwright assertion can target the
            filtered-empty case.
          */}
          <p className="text-sm font-medium" data-testid="users-empty-filtered-headline">
            No users match these filters
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            No results in this tenant for the current search and filter
            combination. Adjust filters or clear them to see more results.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={onReset}
            data-testid="users-empty-reset-filters"
          >
            <XIcon className="h-3 w-3 mr-1.5" /> Clear filters
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm font-medium" data-testid="users-empty-no-users-headline">
            No users yet
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Invite the first user to start collaborating on ArgusAI.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function UserRow({
  user, selected, onToggle, onOpen, showClearance, isSelf,
}: {
  user: User;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  showClearance: boolean;
  /** The signed-in operator's own row — their lifecycle is somebody else's. */
  isSelf: boolean;
}) {
  const isReviewOverdue = user.nextReviewDue && new Date(user.nextReviewDue).getTime() < Date.now();
  const isDormant = user.dormancyDays >= 30 && user.status === 'active';

  // ── B2c-T3 — the row menu's lifecycle actions come from the server ───────
  //
  // The menu used to offer "View details / Copy email / Manage on detail
  // page…" and nothing else, with a comment explaining that everything
  // security-sensitive lived on the detail page because the row had nowhere to
  // put a confirmation, a reason capture or a refusal. It does now: the
  // shared lifecycle control owns all three, and the entries come from
  // `GET /users/:id/lifecycle` — the same projection the detail page renders.
  // This row decides nothing: not which actions exist, not their words, not
  // their emphasis, not whether they prompt, not why a refused one is refused.
  //
  // The question is asked when the menu OPENS, and only then: a roster page
  // carries up to 500 rows and nobody wants 500 round trips for a menu they
  // may never open. `menuTouched` latches so the answer survives the menu
  // closing — the confirmation dialog it opened is still on screen.
  //
  // …and so does the MACHINERY (review B2cT3-BF-04). `enabled: menuTouched`
  // held the requests back but the row still mounted the runner on paint, and
  // the runner is two `useMutation` observers, four `useState` and — through
  // `dialogs` — an AlertDialog plus a TypedConfirmDialog with four more
  // `useState` and an effect. At `pageSize: 500` that is a thousand mutation
  // observers and a thousand dialog roots before anybody opens a menu, and it
  // is re-evaluated on every keystroke in the search box. The whole block now
  // mounts with the menu, once, and stays mounted: `MenuAndDialogs` below is
  // what latching actually buys.
  const [menuTouched, setMenuTouched] = useState(false);

  return (
    <tr
      className={cn(
        'group border-b text-xs cursor-pointer transition-colors',
        selected ? 'bg-primary/5' : 'hover:bg-muted/30',
      )}
      onClick={onOpen}
    >
      <td className="px-3 py-2.5 align-middle" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={onToggle} aria-label={`Select ${user.fullName}`} />
      </td>

      {/* Name / Email / Employment type (inline chip) */}
      <td className="px-2 py-2.5 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <UserAvatar name={user.fullName} color={user.avatarColor} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate flex items-center gap-1.5">
              {user.fullName}
              {isDormant && (
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AlertCircle className="h-3 w-3 text-amber-500 shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent className="text-xs">
                      Dormant: no sign-in for {user.dormancyDays} days
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
              <span className="truncate">{user.email}</span>
              {/*
                B2c-T2 — the roster flags a TIME-BOUNDED engagement and
                leaves permanent staff unlabelled, which is what
                `!== 'fte'` meant. The fact is declared on the employment
                type (`isTimeBoundedEmployment`), so the row does not
                re-derive it from a key, and a null type renders nothing.
              */}
              {isTimeBoundedEmployment(user.employmentType) && (
                <EmploymentTypeBadge type={user.employmentType} />
              )}
            </div>
          </div>
        </div>
      </td>

      <td className="px-2 py-2.5 align-middle">
        <UserStatusBadge status={user.status} />
      </td>

      <td className="px-2 py-2.5 align-middle">
        {/* B2b-T3 — the LIST, on the same row and in the same column. An
            all-departments person reads "All departments"; somebody in none
            reads an em-dash. Same helper the detail page and the wizard use. */}
        <span className="truncate text-muted-foreground block">{formatUserDepartments(user.departments)}</span>
      </td>

      <td className="px-2 py-2.5 align-middle">
        <div className="flex items-center gap-1 min-w-0">
          {user.roles.length === 0 ? (
            <span className="text-muted-foreground italic">No roles</span>
          ) : (
            <>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 truncate">
                {user.roles[0].roleName}
              </Badge>
              {user.roles.length > 1 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-muted">
                  +{user.roles.length - 1}
                </Badge>
              )}
            </>
          )}
        </div>
      </td>

      {showClearance && (
        <td className="px-2 py-2.5 align-middle">
          <ClearanceBadge level={user.clearance.level} />
        </td>
      )}

      {/* Auth: MFA + US Person grouped */}
      <td className="px-2 py-2.5 align-middle">
        <div className="flex items-center justify-center gap-1.5">
          <MfaStatusChip enabled={user.mfaEnabled} method={user.mfaMethod} enforced={user.mfaEnforcedByPolicy} compact />
          <UsPersonIndicator verified={user.usPerson.verified} compact />
        </div>
      </td>

      <td className="px-2 py-2.5 align-middle">
        <div className="flex items-center gap-1">
          <RiskScoreBadge score={user.riskScore} />
          {isReviewOverdue && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Clock className="h-3 w-3 text-red-500 shrink-0" />
                </TooltipTrigger>
                <TooltipContent className="text-xs">Access review overdue</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </td>

      <td className="px-2 py-2.5 align-middle">
        <span className="text-muted-foreground tabular-nums text-[11px]">{relativeTime(user.lastLoginAt)}</span>
      </td>

      {/* Actions — sticky right so the column is reachable on tablet (≤768px)
          where the fixed-layout table is wider than the viewport and the
          row scrolls horizontally. The bg matches the row state (selected /
          hover / default) so it visually aligns with the cell behind it. */}
      <td
        className={cn(
          'px-2 py-2.5 align-middle sticky right-0 z-[5] border-l border-border/40 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] transition-colors',
          selected ? 'bg-primary/5' : 'bg-card group-hover:bg-muted/30',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end gap-0">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" title="View details" onClick={onOpen}>
          <Eye className="h-3.5 w-3.5" />
        </Button>
        {menuTouched ? (
          // Mounted on the first touch and never unmounted again: the lifecycle
          // query, the runner and its confirmation dialogs all live in here.
          <UserRowActionsMenu
            user={user}
            isSelf={isSelf}
            onOpen={onOpen}
            menuTouched={menuTouched}
          />
        ) : (
          // The untouched row is a BUTTON, not a menu: no query, no mutation
          // observers, no dialog roots. Pressing it mounts the real menu
          // already open, so the first click still opens it (review
          // B2cT3-BF-04).
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 shrink-0"
            title="More actions"
            aria-haspopup="menu"
            onClick={() => setMenuTouched(true)}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        )}
        </div>
      </td>
    </tr>
  );
}

/**
 * The row's More menu, its lifecycle answer and its confirmations.
 *
 * Its own component because it is only mounted once the operator has touched
 * the menu (review B2cT3-BF-04). `useLifecycleTransition` mounts two mutation
 * observers and, through `dialogs`, an AlertDialog and the rail's
 * TypedConfirmDialog; a roster page holds up to 500 rows and every one of them
 * paid for that on first paint and on every filter keystroke, for a menu almost
 * none of them would open. `enabled: menuTouched` already stopped the 500
 * REQUESTS; this stops the 500 copies of the machinery.
 */
function UserRowActionsMenu({
  user, isSelf, onOpen, menuTouched,
}: {
  user: User;
  isSelf: boolean;
  onOpen: () => void;
  /** Always true — this component is not mounted until the menu is touched. */
  menuTouched: boolean;
}) {
  // Mounts OPEN: the click that mounted this component was the click on the
  // trigger, and it must not be swallowed.
  const [menuOpen, setMenuOpen] = useState(true);
  const {
    lifecycle,
    isLoading: lifecycleLoading,
    error: lifecycleError,
  } = useUserLifecycle(user.id, { enabled: menuTouched });
  const lifecycleRunner = useLifecycleTransition({
    id: user.id,
    fullName: user.fullName,
    email: user.email,
  });

  const rowAllowed = (lifecycle?.allowed ?? []).filter((t) =>
    ROW_LIFECYCLE_SURFACES.includes(t.surface)
  );
  const rowRefused = isSelf
    ? []
    : (lifecycle?.refused ?? []).filter((t) => ROW_LIFECYCLE_SURFACES.includes(t.surface));

  // A SECTION, or no section at all (review B2cT3-R6 / BF-07). Both lists are
  // legitimately empty — on your own row every status and delete edge is
  // refused for separation of duties and the refusals are not drawn, and a
  // `deleted` account has no edges left — so an unconditional rule left a
  // divider with nothing beneath it on every operator's own row.
  const hasLifecycleSection =
    lifecycleLoading || !!lifecycleError || rowAllowed.length > 0 || rowRefused.length > 0;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" title="More actions">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
            {user.fullName}
          </DropdownMenuLabel>
          {/*
            Closes #130 — the row More menu used to expose Reset password,
            Reset MFA, Terminate sessions, Lock/Unlock, Resend invite, and
            Deactivate, each backed by a toast.success() no-op. Those
            actions are security-sensitive and audit-sensitive — they
            require reason capture, confirmation dialogs, self-action
            guards, and the lifecycle state machine.

            B2c-T3 — that is still the rule, and it is now SATISFIED here
            rather than deferred: every lifecycle action below is the
            server's own entry, fired through the one shared control, with
            its confirmation, its reason capture and (for the removal) the
            rail's typed-confirm gate. What the menu still refuses to invent
            is an action the server did not offer.
          */}
          <DropdownMenuItem onSelect={onOpen} className="text-xs">
            <Eye className="h-3.5 w-3.5 mr-2" /> View details
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              navigator.clipboard.writeText(user.email);
              toast.success('Email copied');
            }}
            className="text-xs"
          >
            <Copy className="h-3.5 w-3.5 mr-2" /> Copy email
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onOpen} className="text-xs">
            <UserCog className="h-3.5 w-3.5 mr-2" /> Manage on detail page…
          </DropdownMenuItem>

          {hasLifecycleSection && <DropdownMenuSeparator />}
          {/* IN FLIGHT is a loading state; FAILED is not (review B2cT3-R2 /
              BF-02). The spinner was keyed on the absence of data, so a 404 for
              a row a stale roster still holds — or any 500 — left the menu
              spinning "Loading actions…" for as long as the page was open,
              after TanStack had given up retrying. The one control in the rail
              that revokes sessions and removes people cannot report a failure
              as work in progress. */}
          {lifecycleLoading && (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Loading actions…
            </DropdownMenuItem>
          )}
          {!lifecycleLoading && !!lifecycleError && (
            <DropdownMenuItem
              disabled
              className="text-xs text-muted-foreground"
              title={getApiErrorMessage(
                lifecycleError,
                'The server did not answer what can be done with this account.'
              )}
            >
              <AlertCircle className="h-3.5 w-3.5 mr-2 text-amber-500" /> Actions unavailable —{' '}
              {getApiErrorMessage(lifecycleError, 'could not be loaded')}
            </DropdownMenuItem>
          )}
          {rowAllowed.map((t) => (
            <DropdownMenuItem
              key={`allow-${t.to}`}
              className={cn(
                'text-xs',
                t.severity === 'destructive' && 'text-red-600 dark:text-red-400',
              )}
              title={t.warning ?? t.label}
              disabled={lifecycleRunner.pending}
              onSelect={(e) => {
                // Close the menu ourselves and open the confirmation: the
                // dialogs are mounted OUTSIDE the menu, so they outlive it.
                e.preventDefault();
                setMenuOpen(false);
                lifecycleRunner.start(t);
              }}
            >
              <LifecycleIcon icon={t.icon} /> {t.label}
            </DropdownMenuItem>
          ))}
          {/* A refused action is SHOWN, disabled, with the server's reason.
              Hiding it tells the operator nothing and produces the support
              ticket behind #75 — and on the roster it is the difference
              between "this build cannot lock people" and "you do not hold
              the key this edge costs". Not on your own row: separation of
              duties refuses every edge there, and a menu of dead items is
              not an explanation. */}
          {rowRefused.map((t) => (
            <DropdownMenuItem
              key={`refuse-${t.to}`}
              disabled
              title={t.reason}
              className="text-xs text-muted-foreground"
            >
              <LifecycleIcon icon={t.icon} /> {t.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* The confirmations live outside the menu: a Radix menu unmounts its
          content when it closes, and a dialog that unmounts as it opens is a
          destructive action nobody can confirm. */}
      {lifecycleRunner.dialogs}
    </>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 60) return minutes < 1 ? 'Just now' : `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
